import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import { TextField, IconButton, InputAdornment } from '@material-ui/core';
import EventIcon from '@material-ui/icons/Event';

/**
 * Date input component with automatic formatting and validation.
 *
 * @component
 * @example
 * // Example usage with Brazilian format
 * <DateInput
 *   label="Birth Date"
 *   value="1990-05-21"
 *   onChange={(newDate) => console.log(newDate)}
 *   charLanguage={true}
 * />
 *
 * @param {Object} props - Component props.
 * @param {string} props.label - Label text displayed above the input field.
 * @param {string|Date} props.value - Current date value (ISO string or Date object).
 * @param {function} props.onChange - Callback fired when a valid date is entered.
 *   Receives the date as an ISO string.
 * @param {boolean} props.charLanguage - Defines the date format:
 *  - `true`: DD/MM/YYYY (Brazilian format).
 *  - `false`: YYYY-MM-DD (international/ISO format).
 * @param {string} [props.dateFormat] - Explicit moment format, overriding `charLanguage`. Pass
 *   the active locale's own convention (`t('formats.date')`) when the surrounding text also
 *   renders dates that way, so the field and the messages beside it agree.
 * @param {boolean} [props.showCalendarPicker=false] - When true, shows a calendar
 *   icon button that opens the browser's native date picker instead of requiring
 *   the date to be typed.
 * @param {Object} [props.props] - Additional props passed down to the Material-UI `TextField`.
 *
 * @returns {JSX.Element} A text field with date masking and validation.
 */
const DateInput = ({
  label,
  value,
  onChange = () => {},
  onBlur = () => {},
  charLanguage,
  dateFormat,
  showCalendarPicker = false,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState(false);
  const nativeDateRef = useRef(null);

  /** Format string used for parsing and displaying the date. @type {string} */
  const format = dateFormat || (charLanguage ? 'DD/MM/YYYY' : 'YYYY-MM-DD');

  useEffect(() => {
    if (moment(value).isValid()) {
      setInputValue(moment(value).format(format));
      setError(false);
    } else {
      setInputValue('');
    }
  }, [value, charLanguage, dateFormat]);

  /**
   * Handles input value changes by applying formatting,
   * validating the date, and triggering the `onChange` callback.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the input field.
   */
  const handleChange = (e) => {
    let val = e.target.value.replace(/\D/g, '');

    if (charLanguage) {
      if (val.length > 2 && val.length <= 4) {
        val = val.slice(0, 2) + '/' + val.slice(2);
      } else if (val.length > 4) {
        val = val.slice(0, 2) + '/' + val.slice(2, 4) + '/' + val.slice(4, 8);
      }
    } else {
      if (val.length > 4 && val.length <= 6) {
        val = val.slice(0, 4) + '-' + val.slice(4);
      } else if (val.length > 6) {
        val = val.slice(0, 4) + '-' + val.slice(4, 6) + '-' + val.slice(6, 8);
      }
    }

    setInputValue(val);

    const parsed = moment(val, format, true);
    if (parsed.isValid()) {
      setError(false);
      onChange(parsed.toISOString());
    } else {
      setError(true);
    }
  };

  const handleBlur = () => {
    const parsed = moment(inputValue, format, true);
    if (parsed.isValid()) onBlur(parsed.toISOString());
  };

  /**
   * Opens the browser's native date picker on the hidden `<input type="date">`,
   * using `showPicker()` where supported and falling back to a focus/click for
   * browsers that don't implement it yet.
   */
  const openCalendar = () => {
    const el = nativeDateRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      el.showPicker();
    } else {
      el.focus();
      el.click();
    }
  };

  /**
   * Handles a date picked from the native calendar (always YYYY-MM-DD),
   * forwarding it through the same onChange contract as manual typing.
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const handleNativeDateChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const parsed = moment(val, 'YYYY-MM-DD', true);
    if (parsed.isValid()) {
      setError(false);
      setInputValue(parsed.format(format));
      onChange(parsed.toISOString());
      onBlur(parsed.toISOString());
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <TextField
        label={label}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={format}
        error={error}
        helperText={error ? 'Invalid date' : ''}
        InputLabelProps={{ shrink: true }}
        InputProps={
          showCalendarPicker
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={openCalendar} aria-label="open calendar">
                      <EventIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : undefined
        }
        {...props}
      />
      {showCalendarPicker && (
        <input
          ref={nativeDateRef}
          type="date"
          value={moment(value).isValid() ? moment(value).format('YYYY-MM-DD') : ''}
          onChange={handleNativeDateChange}
          style={{ position: 'absolute', bottom: 0, left: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          tabIndex={-1}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default DateInput;
